import { call, fork, put, select } from 'redux-saga/effects';
import { SignalingSocket } from 'services/signaling';
import { logger } from 'utils/logger';
import { renameUserRequested, userRenamed } from 'features/users/actions';
import { selectE2eeEnabled } from 'features/e2ee/e2eeSlice';
import { bulkExchangeEncryptedNameSaga } from 'features/e2ee/sagas/bulkExchangeEncryptedNameSaga';
import { User } from 'features/users/types';
import { selectActiveUserById, selectUsersWithoutLocal } from 'features/users/usersSlice';
import { E2EEManager } from 'features/e2ee/E2EEManager';
import { selectRoomId } from 'features/room/roomSlice';
import { e2eeEncrypt } from 'features/e2ee/utils/e2eeEncrypt';

export function* renameUserSaga(action: ReturnType<typeof renameUserRequested>) {
  const { id, name, local } = action.payload;

  const e2eeEnabled: boolean = yield select(selectE2eeEnabled);
  const renamedTo = e2eeEnabled ? '' : ` to ${name}`;

  if (local) {
    logger.remote({ action: true }).info(`The user renamed himself${renamedTo}`);
  } else {
    logger
      .remote({ action: true })
      .info(`The user renamed a participant with id=${id}${renamedTo}`);
  }

  if (e2eeEnabled) {
    if (local) {
      yield put(
        userRenamed({
          id,
          name,
        })
      );

      yield call(SignalingSocket.send, {
        event: 'renameUser',
        data: {
          id,
        },
      });

      const roomUsers: User[] = yield select(selectUsersWithoutLocal);
      yield fork(bulkExchangeEncryptedNameSaga, roomUsers, { rename: true });

      return;
    }

    const targetUser: User | undefined = yield select(selectActiveUserById, id);
    if (!targetUser?.name) {
      return;
    }

    const roomId: string = yield select(selectRoomId);
    const sharedSecret: CryptoKey = yield call(
      E2EEManager.exchangeEncryptionKey,
      id,
      roomId,
      targetUser.e2eePublicKey!
    );

    const encryptedUsername: string = yield call(e2eeEncrypt, name, sharedSecret);

    yield call(SignalingSocket.send, {
      event: 'renameUser',
      data: {
        id,
        name: encryptedUsername,
      },
    });

    return;
  }

  yield call(SignalingSocket.send, {
    event: 'renameUser',
    data: {
      id,
      name,
    },
  });
}
